import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ContactDetailsForm = ({ formData, onFormDataChange, errors }) => {
  const handleInputChange = (field, value) => {
    onFormDataChange({
      ...formData,
      [field]: value
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Contact Details</h2>
        <p className="text-muted-foreground">
          Provide your contact information for project communication
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Primary Contact */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Primary Contact</h3>
          </div>

          <div className="space-y-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter your full name"
              value={formData?.contactName}
              onChange={(e) => handleInputChange('contactName', e?.target?.value)}
              error={errors?.contactName}
              required
            />

            <Input
              label="Job Title"
              type="text"
              placeholder="Your position/title"
              value={formData?.jobTitle}
              onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
              error={errors?.jobTitle}
              required
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="your.email@company.com"
              value={formData?.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e?.target?.value)}
              error={errors?.contactEmail}
              required
            />

            <Input
              label="Phone Number"
              type="tel"
              placeholder="+256 XXX XXX XXX"
              value={formData?.contactPhone}
              onChange={(e) => handleInputChange('contactPhone', e?.target?.value)}
              error={errors?.contactPhone}
              required
            />

            <Select
              label="Preferred Contact Method"
              placeholder="How should we contact you?"
              required
              options={[
                { value: 'email', label: 'Email' },
                { value: 'phone', label: 'Phone Call' },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'video-call', label: 'Video Call' }
              ]}
              value={formData?.preferredContact}
              onChange={(value) => handleInputChange('preferredContact', value)}
              error={errors?.preferredContact}
            />

            <Select
              label="Best Time to Contact"
              placeholder="When is the best time to reach you?"
              options={[
                { value: 'morning', label: 'Morning (8AM - 12PM)' },
                { value: 'afternoon', label: 'Afternoon (12PM - 5PM)' },
                { value: 'evening', label: 'Evening (5PM - 8PM)' },
                { value: 'anytime', label: 'Anytime during business hours' }
              ]}
              value={formData?.bestContactTime}
              onChange={(value) => handleInputChange('bestContactTime', value)}
              error={errors?.bestContactTime}
            />
          </div>
        </div>

        {/* Company Information */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-accent/10 rounded-lg">
              <Icon name="Building" size={20} className="text-accent" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Company Information</h3>
          </div>

          <div className="space-y-4">
            <Input
              label="Company Name"
              type="text"
              placeholder="Your company name"
              value={formData?.companyName}
              onChange={(e) => handleInputChange('companyName', e?.target?.value)}
              error={errors?.companyName}
              required
            />

            <Select
              label="Industry"
              placeholder="Select your industry"
              required
              searchable
              options={[
                { value: 'government', label: 'Government' },
                { value: 'healthcare', label: 'Healthcare' },
                { value: 'education', label: 'Education' },
                { value: 'finance', label: 'Finance & Banking' },
                { value: 'manufacturing', label: 'Manufacturing' },
                { value: 'retail', label: 'Retail & Commerce' },
                { value: 'agriculture', label: 'Agriculture' },
                { value: 'telecommunications', label: 'Telecommunications' },
                { value: 'transportation', label: 'Transportation' },
                { value: 'energy', label: 'Energy & Utilities' },
                { value: 'ngo', label: 'NGO/Non-Profit' },
                { value: 'other', label: 'Other' }
              ]}
              value={formData?.industry}
              onChange={(value) => handleInputChange('industry', value)}
              error={errors?.industry}
            />

            <Select
              label="Company Size"
              placeholder="Select company size"
              required
              options={[
                { value: 'startup', label: 'Startup (1-10 employees)' },
                { value: 'small', label: 'Small (11-50 employees)' },
                { value: 'medium', label: 'Medium (51-200 employees)' },
                { value: 'large', label: 'Large (201-1000 employees)' },
                { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
              ]}
              value={formData?.companySize}
              onChange={(value) => handleInputChange('companySize', value)}
              error={errors?.companySize}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Company Address
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                placeholder="Enter your company address"
                value={formData?.companyAddress}
                onChange={(e) => handleInputChange('companyAddress', e?.target?.value)}
              />
              {errors?.companyAddress && (
                <p className="mt-1 text-sm text-error">{errors?.companyAddress}</p>
              )}
            </div>

            <Input
              label="Website (Optional)"
              type="url"
              placeholder="https://www.yourcompany.com"
              value={formData?.companyWebsite}
              onChange={(e) => handleInputChange('companyWebsite', e?.target?.value)}
              error={errors?.companyWebsite}
            />
          </div>
        </div>
      </div>
      {/* Additional Contact & Decision Making */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-secondary/10 rounded-lg">
            <Icon name="Users" size={20} className="text-secondary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Decision Making & Additional Contacts</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Decision Making Authority"
              placeholder="Your role in decision making"
              required
              options={[
                { value: 'final-decision', label: 'I make the final decision' },
                { value: 'recommend', label: 'I recommend, others decide' },
                { value: 'influence', label: 'I influence the decision' },
                { value: 'gather-info', label: 'I gather information only' }
              ]}
              value={formData?.decisionAuthority}
              onChange={(value) => handleInputChange('decisionAuthority', value)}
              error={errors?.decisionAuthority}
            />

            <Input
              label="Budget Approval Authority"
              type="text"
              placeholder="Who approves the budget for this project?"
              value={formData?.budgetApprover}
              onChange={(e) => handleInputChange('budgetApprover', e?.target?.value)}
              error={errors?.budgetApprover}
            />
          </div>

          <div className="space-y-4">
            <Input
              label="Additional Contact Name (Optional)"
              type="text"
              placeholder="Name of additional stakeholder"
              value={formData?.additionalContactName}
              onChange={(e) => handleInputChange('additionalContactName', e?.target?.value)}
              error={errors?.additionalContactName}
            />

            <Input
              label="Additional Contact Email (Optional)"
              type="email"
              placeholder="additional.contact@company.com"
              value={formData?.additionalContactEmail}
              onChange={(e) => handleInputChange('additionalContactEmail', e?.target?.value)}
              error={errors?.additionalContactEmail}
            />
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <label className="block text-sm font-medium text-foreground">
            Communication Preferences
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              label="Include me in all project communications"
              checked={formData?.includeInCommunications}
              onChange={(e) => handleInputChange('includeInCommunications', e?.target?.checked)}
            />
            <Checkbox
              label="Send project updates to additional contact"
              checked={formData?.sendUpdatesToAdditional}
              onChange={(e) => handleInputChange('sendUpdatesToAdditional', e?.target?.checked)}
            />
            <Checkbox
              label="I agree to receive marketing communications"
              checked={formData?.marketingConsent}
              onChange={(e) => handleInputChange('marketingConsent', e?.target?.checked)}
            />
            <Checkbox
              label="I agree to the terms and conditions"
              checked={formData?.termsAccepted}
              onChange={(e) => handleInputChange('termsAccepted', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailsForm;